import * as d3 from "d3";
import ForcesGenerator from "./ForceGenerators/ForcesGenerator";
import KinematicsManager from "../KinematicsManager";
import MovementGenerator from "./MovementGenerator";
import DragGenerator from "./DragGenerator";

export default class SimulationGenerator {
    // Public:
    constructor(cParams) {
        this.saveConstructionParams(cParams);
        this.createDragFunction();
    }

    compute(cParams) {
        this.saveComputeParams(cParams);
        this.createForcesGenerator();
        this.createFunction();
        this.createKinematicsManager();
        this.attachMovementFunctionToNodes();
        this.attachForceToLinks();
        return this._func;
    }

    restartAlphaTarget() {
        // let aT = this._initialAlphaTarget
        let aT = this._finalAlphaTarget;
        this._func.alphaTarget(aT).restart();
    }

    setFinalAlphaTarget() {
        let aT = this._finalAlphaTarget;
        this._func.alphaTarget(aT);
    }

    setAlphaDecay() {
        let aD = this._alphaDecay;
        this._func.alphaDecay(aD);
    }

    setGravitationalCenter(center) {
        let forceGen = this._forceGen;
        let kinematicsManager = this._kinematicsManager;
        const generatorExists = forceGen !== undefined;
        const kinematicsManagerExists = kinematicsManager !== undefined;
        if (generatorExists) forceGen.setGravitationalCenter(center);
        if (kinematicsManagerExists) kinematicsManager.setGravitationalCenter(center);
    }

    // Private:
    saveConstructionParams(cParams) {
        this._elementId = cParams.elementId;
        this._alphaDecay = cParams.alphaTarget.decayRate;
        this._initialAlphaTarget = cParams.alphaTarget.initial;
        this._finalAlphaTarget = cParams.alphaTarget.final;
        this._velocityDecayParams = cParams.velocityDecay;
        this._vorticityThreshold = cParams.vorticityThreshold;
        this._maxVelocity = cParams.maxVelocity;
        this._physicalParams = cParams.physics;
        this._textVerticalPadding = cParams.textSettings.verticalPadding;
    }

    createForcesGenerator() {
        let cParams = this._physicalParams;
        cParams.activeNodes = this._activeNodes;
        cParams.inputNodes = this._inputNodes;
        cParams.clusters = this._clusters;
        cParams.superClusters = this._superClusters;
        cParams.sharedNodesClusters = this._sharedNodesClusters;
        cParams.initNodesState = this._initNodesState;
        let forceGen = new ForcesGenerator(cParams);
        this._forceGen = forceGen;
    }

    createFunction() {
        this._func = d3.forceSimulation().restart();
        this.setupAlpha();
        this.setNodeForces();
        this.setLinkForce();
        if (this.isCenteringForceEnabled()) this.setCenteringForce();
    }

    setNodeForces() {
        let forceGen = this._forceGen;
        let chargeForce = forceGen.createChargeForce();
        let collisionForce = forceGen.createCollisionForce();
        let positioningForce = forceGen.createPositioningForce();
        let orbitalForces = forceGen.createOrbitalForces();
        let orbitalReactions = forceGen.createOrbitalReactions();
        let orbitalSharedNodesForces = forceGen.createSharedNodesOrbitalForces();
        let clusterRepulsiveForces = forceGen.createClusterRepulsiveForces();
        let superClusterRepulsiveForces = forceGen.createSuperClusterRepulsiveForces();
        this._func
            .force("charge", chargeForce)
            .force("collision", collisionForce)
            .force("positioningX", positioningForce.x)
            .force("positioningY", positioningForce.y);

        orbitalForces.forEach(orbitalForce => {
            let name = orbitalForce.name;
            let force = orbitalForce.force;
            this._func.force(name, force);
        });

        orbitalReactions.forEach(orbitalReaction => {
            let name = orbitalReaction.name;
            let force = orbitalReaction.force;
            this._func.force(name, force);
        });

        orbitalSharedNodesForces.forEach(orbitalSharedNodesForce => {
            let name = orbitalSharedNodesForce.name;
            let force = orbitalSharedNodesForce.force;
            this._func.force(name, force);
        });

        clusterRepulsiveForces.forEach(clusterRepulsiveForce => {
            let name = clusterRepulsiveForce.name;
            let force = clusterRepulsiveForce.force;
            this._func.force(name + "X", force.x).force(name + "Y", force.y);
        });

        superClusterRepulsiveForces.forEach(superClusterRepulsiveForce => {
            let name = superClusterRepulsiveForce.name;
            let force = superClusterRepulsiveForce.force;
            this._func.force(name + "X", force.x).force(name + "Y", force.y);
        });
    }

    setLinkForce() {
        let linkForce = this._forceGen.createLinkForce();
        this._func.force("link", linkForce);
    }

    setCenteringForce() {
        let centeringForce = this._forceGen.createCenteringForce();
        this._func.force("centering", centeringForce);
    }

    setupAlpha() {
        this.setAlphaDecay();
        this.restartAlphaTarget();
        this.setFinalAlphaTarget();
    }

    saveComputeParams(cParams) {
        this._activeNodes = cParams.activeNodes;
        this._activeLinks = cParams.activeLinks;
        this._inputNodes = cParams.inputNodes;
        this._inputLinks = cParams.inputLinks;
        this._nodeFunc = cParams.nodeFunc;
        this._linkFunc = cParams.linkFunc;
        this._clusters = cParams.clusters;
        this._superClusters = cParams.superClusters;
        this._sharedNodesClusters = cParams.sharedNodesClusters;
        this._DEBUGGING_FUNCS = cParams.DEBUGGING_FUNCS;
    }

    createKinematicsManager() {
        const cParams = {
            elementId: this._elementId,
            nodes: this._activeNodes,
            simulationFunc: this._func,
            velocityDecayParams: this._velocityDecayParams,
            vorticityThreshold: this._vorticityThreshold,
            maxVelocity: this._maxVelocity,
        };
        const kinematicsManager = new KinematicsManager(cParams);
        this._kinematicsManager = kinematicsManager;
    }

    attachMovementFunctionToNodes() {
        this.createMovementFunction();
        let nodes = this._activeNodes;
        let movementFunc = this._movementFunc;
        let refreshForcesFunc = () => this.setNodeForces();
        let applyKinematicsControl = () => this.applyKinematicsControl();
        this._func.nodes(nodes).on("tick", function() {
            movementFunc();
            refreshForcesFunc();
            applyKinematicsControl();
        });
    }

    getClonedNodes() {
        let clonedNodes = [];
        let originalNodes = this._activeNodes;
        for (let i = 0; i < originalNodes.length; i++) {
            const node = originalNodes[i];
            let clonedNode = Object.assign({}, node);
            clonedNodes.push(clonedNode);
        }
        return clonedNodes;
    }

    attachForceToLinks() {
        let links = this._activeLinks;
        this._func.force("link").links(links);
    }

    createMovementFunction() {
        this.createMovementGenerator();
        this._movementFunc = this._movementGen.compute();
    }

    createMovementGenerator() {
        let cParams = {
            nodeFunc: this._nodeFunc,
            linkFunc: this._linkFunc,
            textVerticalPadding: this._textVerticalPadding,
            DEBUGGING_FUNCS: this._DEBUGGING_FUNCS,
        };
        this._movementGen = new MovementGenerator(cParams);
    }

    createDragFunction() {
        let cParams = {
            simulationGen: this,
            elementId: this._elementId,
        };
        let dragGen = new DragGenerator(cParams);
        this._dragFunc = dragGen.compute();
    }

    applyKinematicsControl() {
        this._kinematicsManager.compute();
    }

    isCenteringForceEnabled() {
        let itIs = this._physicalParams.centeringForceEnabled;
        return itIs;
    }
}
